import React, { useState, useRef, useEffect } from 'react';
import sparkApi from '../../../api/SparkApi'; // adjust path
import jwt_decode from 'jwt-decode';
import EmojiPicker from 'emoji-picker-react';
import DataApi from '../../../api/DataApi';
import WatconnectApi from '../../../api/WatconnectApi';
import FileAttachmentModal from './FileAttachmentModal';
import TemplateModal from './TemplateModal';

const WhatsAppChat = ({
  isOpen,
  onToggle,
  onClose,
  recipientName,
  recipientPhone,
  socket,
  selectedWhatsAppSetting,
}) => {
  const dataApi = new DataApi('watconnect_history');

  const [userInfo] = useState(
    jwt_decode(localStorage.getItem('watconnect_token'))
  );
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showMemberInfo, setShowMemberInfo] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [receivedMessage, setReceivedMessage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showFileAttachment, setShowFileAttachment] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const messagesEndRef = useRef(null);

  // File icons
  const pdfIcon = '/user_images/pdf-icon-png.jpg';
  const docxIcon = '/user_images/doc-icon.png';
  const xlsIcon = '/user_images/xls-icon.png';
  const mapIcon = '/user_images/google-maps.jpg';
  const textIcon = '/user_images/text-file.jpg';
  // ✅ 1️⃣ Socket listener — only append message directly, don't trigger refetch
  useEffect(() => {
    if (!socket) return;

    socket.on('receivedwhatsappmessage', data => {
      const socketNumber = (
        data?.recipient_id ||
        data?.to ||
        data?.from ||
        ''
      ).replace(/\D/g, '');
      const currentNumber = recipientPhone.replace(/\D/g, '');

      if (socketNumber === currentNumber) {
        getMessageHistoryRecords(selectedWhatsAppSetting);
      }
    });

    return () => socket.off('receivedwhatsappmessage');
  }, [socket, recipientPhone, selectedWhatsAppSetting]);

  // ✅ 2️⃣ Fetch history — only when user opens chat or changes WhatsApp setting
  useEffect(() => {
    if (recipientPhone && selectedWhatsAppSetting) {
      getMessageHistoryRecords(selectedWhatsAppSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientPhone, selectedWhatsAppSetting]);

  // ✅ 3️⃣ Function stays same — no need to touch
  const getMessageHistoryRecords = async selectedWhatsAppSetting => {
    if (recipientPhone && selectedWhatsAppSetting) {
      let result = await dataApi.fetchById(
        `records/${recipientPhone}?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );
      if (result.data?.success) {
        const formatted = (result.data?.records || []).map(r => ({
          ...r.history,
          template: r.template,
          params: r.params,
        }));
        console.log(formatted);
        setMessages(formatted);
      } else {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
  };

  // ✅ 4️⃣ Auto-scroll (no change)
  // ✅ Auto-scroll — Stable fix with delay and cleanup
  useEffect(() => {
    if (!messages.length) return;

    const timeout = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, 200); // Wait 200ms for DOM + media to render

    return () => clearTimeout(timeout);
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = e => {
      // if clicked outside emoji picker and smile icon
      if (
        !e.target.closest('.wa-emoji-picker') &&
        !e.target.closest('.fa-face-smile')
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const onEmojiClick = emojiData => {
    setNewMessage(prev => prev + emojiData.emoji);
    // setShowEmojiPicker(false);
  };

  const handleFileSendComplete = () => {
    // Refresh messages after file send
    getMessageHistoryRecords(selectedWhatsAppSetting);
  };
  const extractParameters = text => {
    if (!text) return [];
    const paramRegex = /\{\{(\d+)\}\}/g;
    const params = [];
    let match;
    while ((match = paramRegex.exec(text)) !== null) {
      params.push(parseInt(match[1]));
    }
    return [...new Set(params)].sort((a, b) => a - b);
  };
  const handleTemplateSend = async submittedData => {
    const log = (msg, data = null) => console.log(`🔹 ${msg}`, data ?? '');
    const logError = (msg, err) =>
      console.error(`❌ ${msg}`, err?.response?.data || err);

    // Utility: fail-fast if condition is false
    const assertSuccess = (condition, message) => {
      if (!condition) throw new Error(message);
    };

    const { selectedTemplate, selectedWhatsAppSetting, parameterValues } =
      submittedData;

    try {
      log('🚀 Sending Template Started');
      const watconnectApi = new WatconnectApi('webhook_template');
      const historyApi = new DataApi('watconnect');

      log('📦 Selected Template:', selectedTemplate);
      log('📱 Recipient:', recipientPhone, recipientName);
      log('⚙️ Selected WhatsApp Setting:', selectedWhatsAppSetting);

      // ========== STEP 1: Upload Media ==========
      const mediaUploads = {};
      const headerComponents =
        selectedTemplate.components?.filter(comp => comp.type === 'HEADER') ||
        [];

      for (const component of headerComponents) {
        if (
          component.format === 'IMAGE' ||
          component.format === 'VIDEO' ||
          component.format === 'DOCUMENT'
        ) {
          try {
            log(`📄 Getting ${component.format} Header from existing file`);

            const fileUrl = selectedTemplate.file_data?.file_url;
            assertSuccess(
              fileUrl,
              `No file URL for ${component.format} header`
            );

            log(`📁 Using existing file: ${fileUrl}`);
            const requestbody = {
              header: component.format,
              header_body: fileUrl,
            };

            const uploadResponse = await watconnectApi.post(
              `/upload_whatsapp_media?whatsapp_setting_number=${encodeURIComponent(
                selectedWhatsAppSetting
              )}`,
              requestbody
            );

            log('📤 Media ID Response:', uploadResponse);

            const mediaId =
              uploadResponse?.data?.id ||
              uploadResponse?.id ||
              uploadResponse?.documentId ||
              uploadResponse?.data?.documentId;

            assertSuccess(
              mediaId,
              `Failed to get media ID for ${component.format} header`
            );

            mediaUploads[`header_${component.format}`] = mediaId;
            log(`✅ Got media ID for ${component.format} header: ${mediaId}`);
          } catch (err) {
            logError(`${component.format} Header Media ID Failed`, err);
            alert(`❌ Media upload failed `);
            setShowTemplateModal(false);
            return; // ⛔ Stop further execution
          }
        }
      }

      // ========== STEP 2: Build WhatsApp Payload ==========
      log('🧩 Building Template Payload Components');

      const templatePayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'template',
        template: {
          name: selectedTemplate.name,
          language: {
            code: selectedTemplate.language || 'en',
            policy: 'deterministic',
          },
          components: [],
        },
      };

      const components = [];

      // HEADER
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'HEADER') {
          const headerComponent = { type: 'header', parameters: [] };
          if (
            component.format === 'IMAGE' ||
            component.format === 'VIDEO' ||
            component.format === 'DOCUMENT'
          ) {
            const mediaId = mediaUploads[`header_${component.format}`];
            if (mediaId) {
              if (
                component.format === 'IMAGE' ||
                component.format === 'VIDEO'
              ) {
                headerComponent.parameters.push({
                  type: component.format.toLowerCase(),
                  [component.format.toLowerCase()]: { id: mediaId },
                });
              } else if (component.format === 'DOCUMENT') {
                headerComponent.parameters.push({
                  type: 'document',
                  document: {
                    id: mediaId,
                    filename:
                      selectedTemplate.file_data?.file_name || 'document.pdf',
                  },
                });
              }
            }
          }
          if (headerComponent.parameters.length > 0)
            components.push(headerComponent);
        }
      });

      // BODY
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'BODY') {
          const bodyComponent = { type: 'body', parameters: [] };
          const params = extractParameters(component.text);
          params.forEach(param => {
            const value = parameterValues[`body_${param}`];
            if (value)
              bodyComponent.parameters.push({ type: 'text', text: value });
          });
          if (bodyComponent.parameters.length > 0)
            components.push(bodyComponent);
        }
      });

      templatePayload.template.components = components;

      log(
        '📨 Final Template Payload:',
        JSON.stringify(templatePayload, null, 2)
      );

      // ========== STEP 3: Send WhatsApp Message ==========
      let messageResponse;
      try {
        log('📤 Sending WhatsApp Template Message...');
        messageResponse = await watconnectApi.post(
          `/message?whatsapp_setting_number=${encodeURIComponent(
            selectedWhatsAppSetting
          )}`,
          templatePayload
        );
        log('📬 Message Response:', messageResponse);
        assertSuccess(
          messageResponse?.messages?.[0]?.id,
          'Failed to send WhatsApp message'
        );
      } catch (err) {
        logError('Message Send Failed', err);
        alert(
          `❌ Failed to send WhatsApp message: ${
            err.response?.data?.message || err.message
          }`
        );
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 4: Create History Record ==========
      let historyRecord;
      try {
        const historyPayload = {
          name: recipientName || '',
          template_name: selectedTemplate.name || '',
          whatsapp_number: recipientPhone.replace(/^\+/, ''),
          message: ``,
          status: 'Outgoing',
          business_number: selectedWhatsAppSetting,
          delivery_status: 'sent',
          message_id: messageResponse?.messages?.[0]?.id,
        };
        log('🗂 Creating History Record:', historyPayload);
        const historyRes = await historyApi.post(`/history`, historyPayload);
        log('🗄 History Response:', historyRes);
        historyRecord = historyRes?.data?.data || historyRes;
        assertSuccess(historyRecord?.id, 'Failed to create message history');
      } catch (err) {
        logError('History Creation Failed', err);
        alert('❌ Failed to create message history.');
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 5: Update Parameters in History ==========
      try {
        const parametersData = {};
        const mainParams = {};

        Object.keys(parameterValues).forEach(key => {
          if (key.startsWith('body_')) {
            const paramNumber = key.split('_')[1];
            const value = parameterValues[key];
            if (value) mainParams[paramNumber] = value;
          }
        });

        if (selectedTemplate.file_data) {
          mainParams.file_url = selectedTemplate.file_data.file_url;
          mainParams.file_type = selectedTemplate.file_data.file_type;
          mainParams.file_name = selectedTemplate.file_data.file_name;
          mainParams.mime_type = selectedTemplate.file_data.mime_type;
        }

        if (Object.keys(mainParams).length > 0)
          parametersData.main = mainParams;

        log('🧩 Updating History Parameters:', parametersData);

        await historyApi.post(`/history`, {
          id: historyRecord.id,
          parameters: parametersData,
        });
      } catch (err) {
        logError('History Parameter Update Failed', err);
        // Note: not a fatal step, history already exists
      }

      log('🎉 Template Sending Completed Successfully!');
    } catch (err) {
      logError('Unhandled Template Send Error', err);
      alert(
        'Failed to send template: ' +
          (err.response?.data?.message || err.message)
      );
    }

    setShowTemplateModal(false);
    getMessageHistoryRecords(selectedWhatsAppSetting);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      // 1️⃣ Prepare WhatsApp payload
      const whatsappPayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'text',
        text: {
          preview_url: false,
          body: newMessage.trim(),
        },
      };

      // 2️⃣ Send message via WhatsApp API
      const watconnectApi = new WatconnectApi('webhook_template');
      const whatsappResponse = await watconnectApi.post(
        `/message?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`,
        whatsappPayload
      );

      // 3️⃣ Prepare DB update payload for WatConnect history
      const updateData = {
        name: recipientName || '',
        whatsapp_number: recipientPhone.replace(/^\+/, ''),
        message: newMessage.trim(),
        status: 'Outgoing',
        business_number: selectedWhatsAppSetting,
        delivery_status: 'sent',
        message_id: whatsappResponse?.messages?.[0]?.id || null,
      };

      // 4️⃣ Save to DB
      const historyApi = new DataApi('watconnect');
      await historyApi.post(`/history`, updateData);

      // 5️⃣ Update UI instantly (no need to wait for API refetch)
      const msgObj = {
        id: Date.now().toString(),
        message: newMessage.trim(),
        status: 'Outgoing',
        delivery_status: 'sent',
        is_read: false,
        created_at: new Date().toISOString(),
        template: null,
        params: null,
      };

      setMessages(prev => [...prev, msgObj]);
      setNewMessage('');
    } catch (error) {
      console.error('❌ Error sending WhatsApp message:', error);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true, // 👈 enables 12-hour AM/PM format
    });
  };

  const getInitials = name => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  const getDateLabel = dateString => {
    const date = new Date(dateString);
    const today = new Date();

    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  const renderMessageContent = msg => {
    const elements = [];

    // Check if this is a template message
    const isTemplate = msg.template && msg.template.template_name;

    if (isTemplate) {
      // ===== TEMPLATE MESSAGE RENDERING =====
      const template = msg.template;
      const params = msg.params || {};

      // 1️⃣ HEADER with Media (Top of template)
      if (
        template.header === 'IMAGE' ||
        template.header === 'VIDEO' ||
        template.header === 'DOCUMENT' ||
        template.header === 'TEXT'
      ) {
        const mainFileUrl = params.main?.file_url;
        const mainFileName = params.main?.file_name || 'Document';
        const mainFileType =
          params.main?.file_type?.toLowerCase().replace('.', '') || '';

        if (template.header === 'TEXT') {
          // TEXT header - use header_body
          if (template.header_body && template.header_body !== 'null') {
            elements.push(
              <div key='template-header-text' className='template-header-text'>
                {template.header_body}
              </div>
            );
          }
        } else if (mainFileUrl) {
          if (template.header === 'IMAGE') {
            elements.push(
              <div key='template-header-img' className='wa-attachment'>
                <img src={mainFileUrl} alt='Header' className='wa-chat-image' />
              </div>
            );
          } else if (template.header === 'VIDEO') {
            elements.push(
              <div key='template-header-vid' className='wa-attachment'>
                <video controls className='wa-chat-video'>
                  <source src={mainFileUrl} type={`video/${mainFileType}`} />
                </video>
              </div>
            );
          } else if (template.header === 'DOCUMENT') {
            // DOCUMENT header for template
            elements.push(
              <div key='template-header-doc' className='wa-attachment'>
                <div className='document-preview'>
                  <div className='document-icon'>📄</div>
                  <div className='document-info'>
                    <div className='document-name'>{mainFileName}</div>
                    <div className='document-size'>
                      {params.main?.file_size
                        ? `(${(params.main.file_size / 1024).toFixed(1)} KB)`
                        : '(Document)'}
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        } else {
          // Fallback if no file URL
          elements.push(
            <div key='template-header-placeholder' className='wa-attachment'>
              <div className='media-preview-placeholder'>
                {template.header === 'IMAGE'
                  ? '🖼️ Image Header'
                  : template.header === 'VIDEO'
                    ? '🎥 Video Header'
                    : '📄 Document Header'}
              </div>
            </div>
          );
        }
      }

      // 2️⃣ BODY (Message content with parameters replaced)
      if (template.message_body) {
        let bodyText = template.message_body;

        // Replace {{1}}, {{2}}, etc. with actual parameter values
        if (params.main) {
          Object.keys(params.main).forEach(key => {
            if (
              key !== 'file_url' &&
              key !== 'file_type' &&
              key !== 'file_name'
            ) {
              bodyText = bodyText.replace(`{{${key}}}`, params.main[key]);
            }
          });
        }

        elements.push(
          <div key='template-body' className='wa-template-body'>
            {bodyText.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }

      // 3️⃣ FOOTER
      if (template.footer && template.footer.trim().toLowerCase() !== 'null') {
        elements.push(
          <div key='template-footer' className='wa-template-footer'>
            {template.footer}
          </div>
        );
      }

      // 4️⃣ CAROUSEL CARDS
      if (template.cards && template.cards.length > 0) {
        const carouselCards = template.cards.map((card, cardIndex) => {
          const cardParams = params.carousel?.[cardIndex] || {};

          return (
            <div key={`carousel-card-${cardIndex}`} className='carousel-card'>
              {/* Card Header Media */}
              {card.header?.format === 'IMAGE' && cardParams.file_url && (
                <div className='carousel-header'>
                  <img
                    src={cardParams.file_url}
                    alt={`Card ${cardIndex + 1}`}
                    className='wa-carousel-media'
                  />
                </div>
              )}
              {card.header?.format === 'VIDEO' && cardParams.file_url && (
                <div className='carousel-header'>
                  <video controls className='wa-carousel-media'>
                    <source src={cardParams.file_url} />
                  </video>
                </div>
              )}

              {/* Card Body */}
              {card.body?.text && (
                <div className='carousel-body'>
                  {card.body.text.split('\n').map((line, i) => {
                    let processedLine = line;
                    // Replace parameters like {{1}}
                    Object.keys(cardParams).forEach(key => {
                      if (
                        key !== 'file_url' &&
                        key !== 'file_type' &&
                        key !== 'file_name'
                      ) {
                        processedLine = processedLine.replace(
                          `{{${key}}}`,
                          cardParams[key]
                        );
                      }
                    });
                    return <div key={i}>{processedLine}</div>;
                  })}
                </div>
              )}

              {/* Card Buttons */}
              {card.buttons && card.buttons.length > 0 && (
                <div className='carousel-buttons'>
                  {card.buttons.map((btn, btnIdx) => (
                    <div key={btnIdx} className='carousel-button'>
                      {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        });

        elements.push(
          <div key='carousel-wrapper' className='template-carousel'>
            <div className='carousel-container'>{carouselCards}</div>
          </div>
        );
      }
    } else {
      // ===== NON-TEMPLATE MESSAGE RENDERING =====

      // --- Media Attachments ---
      if (msg.params?.file_url) {
        let { file_url, file_type } = msg.params;
        file_type = (file_type || '').toLowerCase().replace('.', '');

        const extension = file_url.split('.').pop()?.toLowerCase() || '';
        if (!file_type || file_type === 'undefined' || file_type === 'null') {
          file_type = extension;
        }

        // Normalize to MIME type
        if (file_type === 'mp4') file_type = 'video/mp4';
        else if (['jpeg', 'jpg'].includes(file_type)) file_type = 'image/jpeg';
        else if (file_type === 'png') file_type = 'image/png';
        else if (file_type === 'gif') file_type = 'image/gif';
        else if (file_type === 'pdf') file_type = 'application/pdf';
        else if (file_type === 'doc' || file_type === 'docx')
          file_type = 'application/msword';
        else if (file_type === 'xls' || file_type === 'xlsx')
          file_type = 'application/vnd.ms-excel';
        else if (file_type === 'mp3') file_type = 'audio/mpeg';
        else if (file_type === 'ogg') file_type = 'audio/ogg';
        else if (file_type === 'aac') file_type = 'audio/aac';
        else if (
          !file_type.startsWith('image/') &&
          !file_type.startsWith('video/') &&
          !file_type.startsWith('audio/') &&
          !file_type.startsWith('application/')
        ) {
          if (['jpg', 'jpeg', 'png', 'gif'].includes(extension))
            file_type = 'image/' + extension;
          else if (['mp4', 'mov', 'avi'].includes(extension))
            file_type = 'video/' + extension;
          else if (['mp3', 'ogg', 'aac'].includes(extension))
            file_type = 'audio/' + extension;
          else file_type = 'application/' + extension;
        }

        // IMAGE
        if (file_type.startsWith('image/')) {
          elements.push(
            <div key='img' className='wa-attachment'>
              <a href={file_url} target='_blank' rel='noreferrer'>
                <img
                  src={file_url}
                  alt='attachment'
                  className='wa-chat-image'
                />
              </a>
            </div>
          );
        }
        // VIDEO
        else if (file_type.startsWith('video/')) {
          elements.push(
            <div key='vid' className='wa-attachment'>
              <video controls className='wa-chat-video'>
                <source src={file_url} type={file_type} />
              </video>
            </div>
          );
        }
        // AUDIO
        else if (file_type.startsWith('audio/')) {
          elements.push(
            <div key='aud' className='wa-attachment'>
              <audio controls className='wa-chat-audio'>
                <source src={file_url} type={file_type} />
              </audio>
            </div>
          );
        }
        // DOCS
        else {
          elements.push(
            <div key='doc' className='wa-attachment'>
              <a href={file_url} target='_blank' rel='noreferrer' download>
                <img
                  src={
                    file_type.includes('pdf')
                      ? pdfIcon
                      : file_type.includes('doc')
                        ? docxIcon
                        : file_type.includes('xls')
                          ? xlsIcon
                          : textIcon
                  }
                  alt='File'
                  className='wa-file-icon'
                />
              </a>
            </div>
          );
        }
      }

      // --- Text Message ---
      if (msg.message) {
        elements.push(
          <div key='txt' className='wa-text ps-2'>
            {msg.message.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }
    }

    // --- Buttons (for both template and non-template) ---
    const buttonsData =
      msg?.interactive_details || msg?.buttons || msg?.template?.temp_buttons;
    if (buttonsData && Array.isArray(buttonsData)) {
      elements.push(
        <div key='btns' className='wa-interactive-buttons'>
          {buttonsData.map((btn, idx) => (
            <div key={idx} className='wa-interactive-button'>
              {btn.title || btn.text}
            </div>
          ))}
        </div>
      );
    }

    return <>{elements}</>;
  };

  return (
    <div className='wa-chat-container'>
      {/* Header */}
      <div className='wa-chat-header'>
        <div className='wa-chat-header-left'>
          <button className='wa-back-btn' onClick={onClose}>
            <i className='fa-solid fa-arrow-left'></i>
          </button>
          <div className='wa-chat-avatar'>
            <div className='wa-avatar-initials'>
              {getInitials(recipientName)}
            </div>
          </div>
          <div className='wa-chat-info'>
            <div className='wa-chat-name'>{recipientName || 'Unknown'}</div>
            <div className='wa-chat-status'>{recipientPhone || ''}</div>
          </div>
        </div>
        <div className='wa-chat-header-right'>
          {/* <button className='wa-header-btn'>
            <i className='fa-solid fa-phone'></i>
          </button> */}
          <button
            className='wa-header-btn'
            onClick={() => setShowTemplateModal(true)}
            title='Send Template'
          >
            <i className='fa-solid fa-layer-group'></i>
          </button>

          <button
            className='wa-header-btn'
            onClick={() => setShowMemberInfo(!showMemberInfo)}
          >
            <i className='fa-solid fa-ellipsis-vertical'></i>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className='wa-chat-messages'>
        <div className='wa-messages-container'>
          {messages.map((msg, index) => {
            const currentDate = getDateLabel(msg.created_at);
            const prevMsg = messages[index - 1];
            const prevDate = prevMsg ? getDateLabel(prevMsg.created_at) : null;
            const showDate = currentDate !== prevDate;

            return (
              <React.Fragment key={`${msg.id}-${index}`}>
                {showDate && (
                  <div className='wa-date-separator'>
                    <span>{currentDate}</span>
                  </div>
                )}
                <div
                  className={`wa-message ${
                    msg.status === 'Outgoing' ? 'wa-outgoing' : 'wa-incoming'
                  }`}
                >
                  <div className='wa-message-bubble'>
                    <div className='wa-message-text'>
                      {renderMessageContent(msg)}
                    </div>
                    {msg.delivery_status === 'failed' && msg.err_messaage && (
                      <div className='message-error'>
                        <small className='text-danger'>
                          {msg.err_messaage}
                        </small>
                      </div>
                    )}

                    <div className='wa-message-time'>
                      <span>{formatTime(msg.created_at)}</span>
                      {msg.status === 'Outgoing' && (
                        <>
                          {msg.delivery_status === 'sent' && (
                            <i className='fa-solid fa-check text-secondary'></i>
                          )}
                          {msg.delivery_status === 'delivered' && (
                            <i className='fa-solid fa-check-double text-secondary'></i>
                          )}
                          {msg.delivery_status === 'read' && (
                            <i className='fa-solid fa-check-double text-primary wa-read'></i>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {isTyping && (
            <div className='wa-message wa-incoming'>
              <div className='wa-message-bubble'>
                <div className='wa-typing-indicator'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className='wa-chat-input'>
        <div className='wa-input-container'>
          {/* Left Icons */}
          <div className='wa-left-icons'>
            {/* Emoji */}
            <button
              className='wa-input-btn'
              onClick={() => {
                setShowEmojiPicker(!showEmojiPicker);
                setShowAttachmentMenu(false);
              }}
            >
              <i className='fa-regular fa-face-smile'></i>
            </button>
          </div>

          {/* Textarea */}
          <textarea
            className='wa-message-input'
            placeholder='Type a message'
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            rows='1'
            maxLength={4096}
          />

          {/* Right Button */}
          {/* Right Button Section */}
          <div className='wa-right-icon'>
            {/* Attach Icon beside Mic */}
            <div className='wa-attach-wrapper'>
              <button
                className='wa-input-btn'
                onClick={() => {
                  setShowFileAttachment(!showFileAttachment);
                  setShowEmojiPicker(false);
                }}
              >
                <i className='fa-solid fa-paperclip'></i>
              </button>
            </div>

            {/* Mic or Send Button */}
            {newMessage.trim() ? (
              <button className='wa-send-btn' onClick={handleSendMessage}>
                <i className='fa-solid fa-paper-plane'></i>
              </button>
            ) : (
              <button className='wa-send-btn' disabled>
                <i className='fa-solid fa-paper-plane'></i>
              </button>
            )}
          </div>
        </div>
        {showEmojiPicker && (
          <div className='wa-emoji-picker'>
            <EmojiPicker onEmojiClick={onEmojiClick} />
          </div>
        )}{' '}
      </div>

      {showMemberInfo && (
        <div className='wa-member-info'>
          <div className='wa-member-header'>
            <button
              className='wa-back-btn'
              onClick={() => setShowMemberInfo(false)}
            >
              <i className='fa-solid fa-arrow-left'></i>
            </button>
            <span>Contact Info</span>
          </div>

          <div className='wa-member-content'>
            <div className='wa-member-avatar-section'>
              <div className='wa-member-avatar'>
                <div className='wa-avatar-initials large'>
                  {getInitials(recipientName)}
                </div>
              </div>
              <div className='wa-member-name'>{recipientName || 'Unknown'}</div>
              <div className='wa-member-phone'>+{recipientPhone}</div>
            </div>

            <div className='wa-member-actions'>
              <button className='wa-action-btn'>
                <i className='fa-solid fa-ban'></i>
                <span>Block</span>
              </button>
              <button className='wa-action-btn'>
                <i className='fa-solid fa-triangle-exclamation'></i>
                <span>Report</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {showFileAttachment && (
        <FileAttachmentModal
          isOpen={showFileAttachment}
          onClose={() => setShowFileAttachment(false)}
          recipientPhone={recipientPhone}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          recipientName={recipientName}
          onSendComplete={handleFileSendComplete}
        />
      )}
      {showTemplateModal && (
        <TemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          recipientPhone={recipientPhone}
          recipientName={recipientName}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          onSendComplete={handleTemplateSend}
        />
      )}
    </div>
  );
};

export default WhatsAppChat;
